<template>
  <div>
    <NovelReader
      class="w-full max-h-screen !h-[100dvh]"
      ref="reader"
      :startingChapterId="chapterId"
      :title-entity="title"
      :aggregate="aggregate"
      v-model:settings="novelSettings"
      @bookmark="onBookmark"
      @comment="onComment"
      @return="doReturn()"
    />
  </div>
</template>

<script setup lang="ts">
import { isTitle, NovelHistory, Statistics } from "~/src/api";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  type NovelBookmarkEvent,
  type NovelCommentEvent,
} from "~/src/novel/reader";
import NovelReader from "~/components/novel/NovelReader.vue";
import {
  useNovelChapterGet,
  useNovelReaderTitleChapters,
} from "~/utils/tanstack/novel";
import { useNovelSettings } from "~/stores/novelSettings";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const chapterId = computed(() => (route.params.id as string) ?? "");

const reader = ref<InstanceType<typeof NovelReader>>();

const { data: currentChapter } = useQuery(useNovelChapterGet(chapterId));
const novelSettings = useNovelSettings();

const titleId = computed(
  () => currentChapter.value?.relationships.find(isTitle)?.id ?? "",
);

const queryClient = useQueryClient();
const { markerMutation, invalidate } = useNovelReadMarkerManager(
  queryClient,
  titleId,
);
const { mutate: updateReadMarkers } = markerMutation;

watch(
  () => unref(reader.value?.chapterId),
  (v) => {
    if (v) performViewActions(v);
  },
);

function performViewActions(chapterId: string) {
  console.debug(
    "%cReader%c Performing chapter view actions for %c%s",
    "color:black;background-color:#ff99ff;padding:2px 4px;border-radius:4px;",
    "",
    "font-weight:bold",
    chapterId,
  );
  navigateTo({
    path: linkTo("novel_chapter", chapterId),
    replace: true,
  });

  authStore.getToken().then((token) => {
    if (token)
      return NovelHistory.create(
        { novelChapterId: chapterId, timestamp: Math.floor(Date.now() / 1000) },
        token,
      );
  });
  updateReadMarkers({ add: [chapterId], noRefresh: true });
}

const { data: title } = useQuery(
  useTitleGet(titleId, ["organization", "cover_art"]),
);
const { data: aggregate } = useQuery({
  ...useNovelReaderTitleChapters(titleId),
  enabled: computed(() => !!titleId.value),
});

function doReturn() {
  if (title.value) {
    navigateTo(linkTo(title.value));
  } else router.back();
}

async function onComment(v: NovelCommentEvent) {
  console.log(v);
}

async function onBookmark(v: NovelBookmarkEvent) {
  console.log(v);
}

onMounted(() => {
  invalidate();
  novelSettings.initialize({
    "screen.width": window.innerWidth,
  });
});
</script>

<style scoped lang="postcss"></style>
